import React, { createContext, useContext } from 'react';
import { useTheme, useMediaQuery, GlobalStyles } from '@mui/material';

const DensityContext = createContext();

export const useDensity = () => {
  const context = useContext(DensityContext);
  if (!context) {
    throw new Error('useDensity must be used within a DensityProvider');
  }
  return context;
};

export const DensityProvider = ({ children }) => {
  const theme = useTheme();

  // Detectar diferentes tamanhos de tela
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.between('lg', 'xl'));
  const isLargeDesktop = useMediaQuery(theme.breakpoints.between('xl', 'xxl'));
  const isUltraWide = useMediaQuery(theme.breakpoints.up('xxl'));

  // Configurações de densidade baseadas no tamanho da tela
  const densityConfig = {
    // Mobile: espaçamento confortável
    mobile: {
      spacing: { xs: 2, sm: 3 },
      cardPadding: { xs: 2, sm: 3 },
      textScale: 1,
      iconScale: 1,
      buttonHeight: 44,
    },

    // Tablet: espaçamento moderado
    tablet: {
      spacing: { md: 2.5 },
      cardPadding: { md: 2.5 },
      textScale: 0.95,
      iconScale: 0.95,
      buttonHeight: 42,
    },

    // Desktop: espaçamento reduzido
    desktop: {
      spacing: { lg: 2 },
      cardPadding: { lg: 2 },
      textScale: 0.9,
      iconScale: 0.9,
      buttonHeight: 40,
    },

    // Desktop grande: muito compacto
    largeDesktop: {
      spacing: { xl: 1.5 },
      cardPadding: { xl: 1.5 },
      textScale: 0.85,
      iconScale: 0.85,
      buttonHeight: 38,
    },

    // Ultra wide: super compacto
    ultraWide: {
      spacing: { xxl: 1.2 },
      cardPadding: { xxl: 1.2 },
      textScale: 0.8,
      iconScale: 0.8,
      buttonHeight: 36,
    },
  };

  // Determinar configuração atual
  let currentConfig = densityConfig.mobile;
  let densityLevel = 'comfortable';
  if (isUltraWide) {
    currentConfig = { ...currentConfig, ...densityConfig.ultraWide };
    densityLevel = 'ultra-compact';
  } else if (isLargeDesktop) {
    currentConfig = { ...currentConfig, ...densityConfig.largeDesktop };
    densityLevel = 'very-compact';
  } else if (isDesktop) {
    currentConfig = { ...currentConfig, ...densityConfig.desktop };
    densityLevel = 'compact';
  } else if (isTablet) {
    currentConfig = { ...currentConfig, ...densityConfig.tablet };
    densityLevel = 'normal';
  }

  const value = {
    isMobile,
    isTablet,
    isDesktop,
    isLargeDesktop,
    isUltraWide,
    densityLevel,
    config: currentConfig,

    // Funções utilitárias
    getSpacing: (base = 1) => {
      const multiplier = Object.values(currentConfig.spacing)[0] || 2;
      return base * multiplier;
    },

    getCardPadding: (base = 1) => {
      const multiplier = Object.values(currentConfig.cardPadding)[0] || 2;
      return base * multiplier;
    },

    getTextSize: baseFontSize => {
      return baseFontSize * currentConfig.textScale;
    },

    getIconSize: baseSize => {
      return baseSize * currentConfig.iconScale;
    },

    shouldUseCompactMode: () => isDesktop || isLargeDesktop || isUltraWide,
    shouldUseUltraCompactMode: () => isLargeDesktop || isUltraWide,
  };

  // Estilos globais para densidade
  const densityStyles = (
    <GlobalStyles
      styles={{
        // Aplicar densidade automática aos componentes MUI
        ...(value.shouldUseCompactMode() && {
          '.MuiCard-root': {
            '& .MuiCardContent-root': {
              padding: `${currentConfig.cardPadding.lg || 16}px !important`,
              '&:last-child': {
                paddingBottom: `${
                  currentConfig.cardPadding.lg || 16
                }px !important`,
              },
            },
          },

          '.MuiButton-root': {
            minHeight: `${currentConfig.buttonHeight}px !important`,
            fontSize: `${currentConfig.textScale}rem !important`,
          },

          '.MuiListItemButton-root': {
            minHeight: `${currentConfig.buttonHeight}px !important`,
            paddingTop: `${(currentConfig.buttonHeight - 24) / 2}px !important`,
            paddingBottom: `${
              (currentConfig.buttonHeight - 24) / 2
            }px !important`,
          },

          '.MuiTypography-h1': {
            fontSize: `${2.25 * currentConfig.textScale}rem !important`,
          },
          '.MuiTypography-h2': {
            fontSize: `${1.875 * currentConfig.textScale}rem !important`,
          },
          '.MuiTypography-h3': {
            fontSize: `${1.5 * currentConfig.textScale}rem !important`,
          },
          '.MuiTypography-h4': {
            fontSize: `${1.25 * currentConfig.textScale}rem !important`,
          },
          '.MuiTypography-h5': {
            fontSize: `${1.125 * currentConfig.textScale}rem !important`,
          },
          '.MuiTypography-h6': {
            fontSize: `${1 * currentConfig.textScale}rem !important`,
          },

          '.MuiSvgIcon-root': {
            fontSize: `${1.25 * currentConfig.iconScale}rem !important`,
          },
        }),

        // Ultra compacto para telas muito grandes
        ...(value.shouldUseUltraCompactMode() && {
          '.ultra-compact': {
            transform: `scale(${currentConfig.textScale})`,
            transformOrigin: 'top left',
          },
        }),
      }}
    />
  );

  return (
    <DensityContext.Provider value={value}>
      {densityStyles}
      {children}
    </DensityContext.Provider>
  );
};

// Hook para componentes que precisam de densidade específica
export const useAdaptiveDensity = (componentType = 'default') => {
  const { shouldUseCompactMode } = useDensity();

  const adaptiveStyles = {
    card: {
      padding: shouldUseCompactMode() ? 1.5 : 2,
      margin: shouldUseCompactMode() ? 1 : 1.5,
    },

    form: {
      spacing: shouldUseCompactMode() ? 2 : 3,
      fieldHeight: shouldUseCompactMode() ? 48 : 56,
    },

    list: {
      itemHeight: shouldUseCompactMode() ? 40 : 48,
      iconSize: shouldUseCompactMode() ? 20 : 24,
    },

    toolbar: {
      height: shouldUseCompactMode() ? 56 : 64,
      padding: shouldUseCompactMode() ? 16 : 24,
    },
  };

  return adaptiveStyles[componentType] || adaptiveStyles.default;
};

export default DensityProvider;
